import { Theme } from "@emotion/react";
import { MenuItem, Select, SelectChangeEvent, SxProps, Typography } from "@mui/material";
import { ReactNode } from "react";

export interface SelectOption<TValue extends string | number> {
  value: TValue;
  label: ReactNode;
}

interface SelectFieldProps<TValue extends string | number> {
  value: TValue;
  onChange: (value: TValue) => void;
  options: SelectOption<TValue>[];
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}

const defaultSelectSx: SxProps<Theme> = {
  borderRadius: "8px",
  fontSize: 14,
  height: 44,
  backgroundColor: "#fff",
  "& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0,0,0,0.12)",
  },
};

const SelectField = <TValue extends string | number>({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  fullWidth,
  sx,
}: SelectFieldProps<TValue>) => {
  const isEmpty =
    value === ("" as TValue) || value === (0 as TValue) || value === (-1 as TValue);

  return (
    <Select
      fullWidth={fullWidth}
      displayEmpty
      value={value as unknown as string}
      onChange={(e) => onChange(e.target.value as TValue)}
      disabled={disabled}
      sx={{ ...defaultSelectSx, ...sx }}
      renderValue={(current) => {
        if (isEmpty) {
          return (
            <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
              {placeholder ?? ""}
            </Typography>
          );
        }
        const match = options.find((o) => String(o.value) === String(current));
        return match?.label ?? (current as ReactNode);
      }}
    >
      {options.map((option) => (
        <MenuItem key={String(option.value)} value={option.value as unknown as string}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
};

export default SelectField;